import { LocalStorageKey, LayoutValue } from 'config';
import { BaseUser, ILocation, IRoleEntity, IUser } from 'interfaces';
import { IDiscountState } from 'redux/modules/DiscountEditor/discountEditorTypes';
import SecureLS from 'secure-ls';

const ls = new SecureLS();
class LocalStorageManager {
  private getLocalStorageItem(key: LocalStorageKey): string | null {
    return localStorage.getItem(key);
  }

  private getComplexLocalStorageItem<T>(key: LocalStorageKey) {
    const obj = localStorage.getItem(key);
    return obj ? (JSON.parse(obj) as T) : null;
  }

  private setLocalStorageItem<T>(
    key: LocalStorageKey,
    entity: string | T,
  ): void {
    localStorage.setItem(
      key,
      typeof entity === 'string' ? entity : JSON.stringify(entity),
    );
  }

  setStatisticsCurrentCountry(country: string): void {
    this.setLocalStorageItem(LocalStorageKey.statisticsCurrentCountry, country);
  }

  getStatisticsCurrentCountry(): string | null {
    return this.getLocalStorageItem(LocalStorageKey.statisticsCurrentCountry);
  }

  getCurrentLanguage(): string | null {
    return this.getLocalStorageItem(LocalStorageKey.i18n);
  }

  getLanguage(): string | null {
    return this.getLocalStorageItem(LocalStorageKey.language);
  }

  setLanguage(lang: string): void {
    this.setLocalStorageItem(LocalStorageKey.language, lang);
  }

  getUser(): BaseUser | null {
    return this.getComplexLocalStorageItem<IUser>(LocalStorageKey.user);
  }

  setUser(baseUser: BaseUser): void {
    this.setLocalStorageItem<BaseUser>(LocalStorageKey.user, baseUser);
  }

  getAPlayerStatus(): boolean | null {
    return this.getLocalStorageItem(LocalStorageKey.aPlayerStatus) === 'true';
  }

  setAPlayerStatus(status: boolean): void {
    this.setLocalStorageItem(LocalStorageKey.aPlayerStatus, status);
  }

  getCurrentLocation(): ILocation | null {
    return this.getComplexLocalStorageItem(LocalStorageKey.userLocation);
  }

  setCurrentLocation(location: ILocation) {
    this.setLocalStorageItem(LocalStorageKey.userLocation, location);
  }

  getUserRole(): IRoleEntity | null {
    return ls.get(LocalStorageKey.userRole);
  }

  setUserRole(role: IRoleEntity) {
    ls.set(LocalStorageKey.userRole, role);
  }

  getDiscountEditor(): IDiscountState | null {
    return this.getComplexLocalStorageItem<IDiscountState>(
      LocalStorageKey.discountEditor,
    );
  }

  setDiscountEditor(DE: IDiscountState): void {
    this.setLocalStorageItem(
      LocalStorageKey.discountEditor,
      JSON.stringify(DE),
    );
  }

  setDiscountEditorStep(step: number): void {
    this.setLocalStorageItem(LocalStorageKey.discountEditorStep, step);
  }

  getDiscountEditorStep(): number | null {
    const step = this.getLocalStorageItem(LocalStorageKey.discountEditorStep);

    return Number(step) || null;
  }

  getCardsLayout(): LayoutValue | null {
    const value = this.getLocalStorageItem(LocalStorageKey.cardsLayout);
    return value !== null &&
      Object.values(LayoutValue).includes(value as LayoutValue)
      ? (value as LayoutValue)
      : null;
  }

  setCardsLayout(layout: LayoutValue): void {
    this.setLocalStorageItem(LocalStorageKey.cardsLayout, layout);
  }

  clearDiscountEditor(): void {
    localStorage.removeItem(LocalStorageKey.discountEditor);
    localStorage.removeItem(LocalStorageKey.discountEditorStep);
  }

  clearStorage() {
    const keyToKeep = 'statisticsCurrentCountry';
    const savedValue = localStorage.getItem(keyToKeep);

    localStorage.clear();

    if (savedValue !== null) {
      localStorage.setItem(keyToKeep, savedValue);
    }
  }
}

export const localStorageManager = new LocalStorageManager();
